import { useEffect, useMemo, useState } from "react";
import { AlertCircle, Trash2 } from "lucide-react";
import { Loader } from "@/components/ui/Loader";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import {
  chaoPhotoFileUrl,
  deleteChaoPhoto,
  fetchChaoReportDetail,
  photoCanDelete,
  type ChaoPhotoDto,
  type ChaoReportSummaryDto,
  type ChaoServiceDef,
} from "@/services/chaoDeObra";
import { formatDateTime } from "@/utils/format";
import { CHAO_AMBIENTES_UI, ambienteMeta, matchAmbienteKey } from "./ambientes";
import { EapReportProgressCards } from "./EapReportProgressCards";

interface Props {
  open: boolean;
  onClose: () => void;
  unitId: string;
  report: ChaoReportSummaryDto | null;
  unitLabel?: string;
  onUpdated?: () => void;
}

export function ChaoDeObraReportModal({
  open,
  onClose,
  unitId,
  report,
  unitLabel,
  onUpdated,
}: Props) {
  const [photos, setPhotos] = useState<ChaoPhotoDto[]>([]);
  const [catalog, setCatalog] = useState<ChaoServiceDef[]>([]);
  const [pctMap, setPctMap] = useState<Record<string, number>>({});
  const [generalPct, setGeneralPct] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deletingPhotoId, setDeletingPhotoId] = useState<string | null>(null);

  const reload = () => {
    if (!report || !unitId) return;
    setLoading(true);
    setError(null);
    void fetchChaoReportDetail(unitId, report.id)
      .then((res) => {
        if (!res.success) {
          setError("Não foi possível carregar o relatório.");
          return;
        }
        setPhotos(res.photos ?? []);
        setCatalog(res.serviceCatalog ?? []);
        setPctMap(res.serviceProgress ?? {});
        setGeneralPct(
          Number(res.generalPct) || Number(res.report?.generalPct) || 0,
        );
      })
      .catch((e) => {
        setError(e instanceof Error ? e.message : "Erro ao carregar relatório.");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!open || !report || !unitId) {
      setPhotos([]);
      setCatalog([]);
      setPctMap({});
      setGeneralPct(0);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    void fetchChaoReportDetail(unitId, report.id)
      .then((res) => {
        if (cancelled) return;
        if (!res.success) {
          setError("Não foi possível carregar o relatório.");
          return;
        }
        setPhotos(res.photos ?? []);
        setCatalog(res.serviceCatalog ?? []);
        setPctMap(res.serviceProgress ?? {});
        setGeneralPct(
          Number(res.generalPct) ||
            Number(res.report?.generalPct) ||
            0,
        );
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "Erro ao carregar relatório.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, report, unitId]);

  const catalogKeys = useMemo(() => CHAO_AMBIENTES_UI.map((a) => a.key), []);

  const sections = useMemo(() => {
    const map = new Map<string, ChaoPhotoDto[]>();
    for (const p of photos) {
      const key = matchAmbienteKey(p.ambiente) ?? (p.ambiente || "Outros");
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(p);
    }
    const known = catalogKeys.filter((k) => map.has(k));
    const extra = [...map.keys()].filter((k) => !catalogKeys.includes(k));
    return [...known, ...extra]
      .map((key) => ({
        key,
        photos: [...map.get(key)!].sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt)),
      }))
      .filter((s) => s.photos.length > 0);
  }, [photos, catalogKeys]);

  const onDeletePhoto = async (photoId: string) => {
    if (!window.confirm("Excluir esta foto permanentemente?")) return;
    setDeletingPhotoId(photoId);
    setError(null);
    try {
      const res = await deleteChaoPhoto(photoId);
      if (!res.success) {
        setError(res.error ?? "Falha ao excluir a foto.");
        return;
      }
      reload();
      onUpdated?.();
    } finally {
      setDeletingPhotoId(null);
    }
  };

  const title = report
    ? report.isPendingApproval || report.status === "pending_approval"
      ? "Relatório fotográfico — aguardando aprovação"
      : report.isOpen
        ? "Relatório fotográfico — em andamento"
        : `Relatório fotográfico — ${formatDateTime(report.at)}`
    : "Relatório fotográfico";

  return (
    <Modal open={open} onClose={onClose} title={title} size="full">
      {report && (
        <div className="flex flex-col gap-1 -mt-2 mb-4 text-sm text-slate-600">
          {unitLabel && <p className="font-semibold text-slate-800">{unitLabel}</p>}
          <p>
            {formatDateTime(report.startedAt)}
            {report.startedAt !== report.at && ` — ${formatDateTime(report.at)}`}
          </p>
          <p className="text-xs text-slate-500">
            {report.photoCount} foto{report.photoCount !== 1 ? "s" : ""} · {report.responsavel}
            {(report.isPendingApproval || report.status === "pending_approval")
              ? " · aguardando aprovação"
              : report.isOpen
                ? " · registro aberto"
                : ""}
          </p>
          <p className="text-xs text-slate-400 mt-2">
            Fotos agrupadas por ambiente — até 3 por linha.
          </p>
        </div>
      )}

      {loading && (
        <div className="flex flex-col items-center justify-center gap-3 py-16 text-slate-500">
          <Loader size="lg" label="Carregando relatório…" />
        </div>
      )}

      {!loading && !error && catalog.length > 0 && (
        <div className="mb-8 pb-8 border-b border-slate-200">
          <EapReportProgressCards
            catalog={catalog}
            pctMap={pctMap}
            generalPct={generalPct}
            lastUpdateAt={report?.at}
            lastUpdateBy={report?.responsavel}
          />
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm mb-4">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {!loading && !error && sections.length === 0 && (
        <p className="text-sm text-slate-500 py-4 text-center">Nenhuma foto neste relatório.</p>
      )}

      {!loading && sections.length > 0 && (
        <>
          <h3 className="text-sm font-bold text-slate-800 mb-4">Fotos por ambiente</h3>
        <div className="flex flex-col gap-8 pb-6">
          {sections.map(({ key, photos: ambPhotos }) => {
            const meta = ambienteMeta(key);
            const Icon = meta.icon;
            return (
              <section key={key} className="flex flex-col gap-3">
                <header className="flex items-center gap-2 sticky top-0 z-10 bg-white/95 backdrop-blur-sm py-2 border-b border-slate-100">
                  <span
                    className={`w-9 h-9 rounded-lg flex items-center justify-center text-white ${meta.bar}`}
                  >
                    <Icon className="w-4 h-4" />
                  </span>
                  <h3 className="text-base font-bold text-slate-900">{meta.label}</h3>
                  <span className="text-xs text-slate-400 ml-auto">
                    {ambPhotos.length} foto{ambPhotos.length !== 1 ? "s" : ""}
                  </span>
                </header>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {ambPhotos.map((p) => (
                    <figure
                      key={p.id}
                      className="relative flex flex-col rounded-xl overflow-hidden border border-slate-200 bg-white shadow-sm min-w-0"
                    >
                      {photoCanDelete(p) && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute top-1.5 right-1.5 z-10 !min-h-0 !h-8 !px-2 border border-red-200 bg-white/95 text-red-700 hover:bg-red-50 shadow-sm"
                          loading={deletingPhotoId === p.id}
                          leftIcon={<Trash2 className="w-3.5 h-3.5" />}
                          onClick={() => void onDeletePhoto(p.id)}
                          aria-label="Excluir foto"
                        >
                          <span className="sr-only">Excluir</span>
                        </Button>
                      )}
                      <a
                        href={chaoPhotoFileUrl(p.id)}
                        target="_blank"
                        rel="noreferrer"
                        className="block bg-slate-100"
                      >
                        <img
                          src={chaoPhotoFileUrl(p.id)}
                          alt={meta.label}
                          className="w-full h-40 sm:h-44 object-contain"
                          loading="lazy"
                        />
                      </a>
                      <figcaption className="px-2.5 py-2 text-[11px] text-slate-600 border-t border-slate-100 leading-snug">
                        <p className="font-semibold text-slate-800 truncate">
                          {formatDateTime(p.createdAt)}
                        </p>
                        <p className="truncate">{p.responsavel}</p>
                        {p.observacao && (
                          <p className="mt-0.5 text-slate-500 italic line-clamp-2">{p.observacao}</p>
                        )}
                      </figcaption>
                    </figure>
                  ))}
                </div>
              </section>
            );
          })}
        </div>
        </>
      )}
    </Modal>
  );
}
